import {
  ComponentType,
  BackOfficeHostingPlatforms,
  EmbeddedScriptPages,
  HeightMode,
  RegionType,
  CustomElementScriptType,
  PanelContentType,
  Width as PanelSizeWidth,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { match } from 'variant';
import { randomUUID } from 'node:crypto';
import uniqWith from 'lodash/uniqWith.js';
import { generateUrl } from '@wix/cli-app-manifest';
import type {
  AppManifest,
  ComponentExperiment,
  BackofficePage,
  BackofficeExtensionWidget,
  BackofficeExtensionMenuItem,
  BackofficeSideCategory,
  BackofficeModal,
  EmbeddedScript,
  CustomElement,
  BlocksWidget,
  Webhook,
  ServicePlugin,
  SitePlugin,
  Topology,
  EcomShippingRates,
  EcomAdditionalFees,
  EcomDiscountsTrigger,
  EcomValidations,
  EcomPaymentSettings,
  EcomGiftCardsProvider,
  EcomRecommendationsProvider,
  SeoKeywordsSuggestion,
  PremiumCustomCharges,
  ExternalDatabaseProvider,
  Monitoring,
  BlocksMainComponent,
  SiteComponent,
  SiteComponentElements,
  PanelComponent,
  StaffSortingProvider,
  BookingsPricingProvider,
} from '@wix/cli-app-manifest';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { RELATIVE_ASSET_PATH_REGEX } from '@wix/cli-app-definitions';
import { dirname, join } from 'node:path';
import { validateComponents } from './validate.js';
import type { DevCenterComponent } from '../dev-center/components/dev-center-component.js';
import type { ComponentSourceBuilder } from './component-source.js';
import { getMonitoringProvider } from './monitoring.js';

interface CreateAppManifestParams {
  appId: string;
  backendServerUrl?: string;
  components: DevCenterComponent[];
  sentryDSN?: string;
  componentSourceBuilder: ComponentSourceBuilder;
}

function isTruthy<T>(x: T | undefined | null | false): x is T {
  return x != null && x !== false;
}

export function parseImageField(asset: string, relativePath: string) {
  if (RELATIVE_ASSET_PATH_REGEX.test(asset)) {
    return generateUrl(join(dirname(`/${relativePath}`), asset));
  }
  return asset;
}

const getExperiment = (
  component: DevCenterComponent<'DashboardPage'>,
  appId: string
): ComponentExperiment => {
  if (component.experiments) {
    return {
      appId,
      componentId: component.id,
      spec: component.experiments.enabledBy,
      variantValue: 'true',
    };
  }
  return undefined;
};

const getHostingPlatform = (hostingPlatform?: string) => {
  return hostingPlatform ?? BackOfficeHostingPlatforms.BUSINESS_MANAGER;
};

export const createCustomElementConfig = (
  component: DevCenterComponent<'CustomElement' | 'SitePlugin'>,
  scriptUrl: string,
  settingsUrl?: string
): CustomElement => {
  const customElementData = match(component, {
    SitePlugin: (sitePlugin) => ({
      id: sitePlugin.referenceComponentId,
      name: sitePlugin.marketData.name,
      base: {
        autoAdd: false,
      },
      size: {
        width: {
          defaultWidth: 400,
          allowStretch: true,
          stretchByDefault: true,
        },
        height: {
          defaultHeight: 400,
          heightMode: HeightMode.FIXED,
        },
      },
      presets: undefined,
      behaviors: {},
    }),
    CustomElement: (customElement) => ({
      id: customElement.id,
      name: customElement.name,
      base: {
        autoAdd: customElement.installation?.autoAddToSite,
        essential: false,
        maxInstances: 0,
      },
      size: {
        width: {
          defaultWidth: customElement.width.defaultWidth,
          allowStretch: customElement.width.allowStretch,
          stretchByDefault: customElement.width.stretchByDefault,
        },
        height: {
          defaultHeight: customElement.height.defaultHeight,
          heightMode: HeightMode.FIXED,
        },
      },
      presets: customElement.presets?.map((preset) => ({
        id: preset.id,
        name: customElement.name,
        thumbnailUrl: parseImageField(
          preset.thumbnailUrl,
          customElement.relativePath
        ),
      })),
      behaviors: customElement.behaviors,
    }),
  });

  return {
    compId: customElementData.id,
    compType: ComponentType.CUSTOM_ELEMENT_WIDGET as const,
    compName: customElementData.name,
    compData: {
      customElementWidget: {
        base: {
          name: customElementData.name,
        },
        widgetData: {
          tagName: component.tagName,
          scriptType: CustomElementScriptType.ES_MODULE,
          scriptUrl,
        },
        size: customElementData.size,
        installation: {
          base: customElementData.base,
          widget: { region: RegionType.BODY },
        },
        behaviors: {
          ...customElementData.behaviors,
          settings:
            component.relativePanelPath && settingsUrl
              ? { settingsUrl }
              : undefined,
        },
        dependencies: component.dependencies,
        presets: customElementData.presets,
      },
    },
  };
};

export async function createAppManifest({
  appId,
  components,
  sentryDSN,
  componentSourceBuilder,
}: CreateAppManifestParams): Promise<AppManifest> {
  validateComponents(components);

  const appManifestComponents = (
    await Promise.all(
      components.map(
        match({
          DashboardPage: async (component): Promise<BackofficePage> => {
            const componentSentryDSN = component.sentryDSN ?? sentryDSN;
            return {
              compId: component.id,
              compType: ComponentType.BACK_OFFICE_PAGE as const,
              compData: {
                backOfficePage: {
                  hostingPlatform: getHostingPlatform(
                    component.hostingPlatform
                  ),
                  fullPage: component.sidebar?.whenActive?.hideSidebar,
                  hideInSidebar: component.sidebar?.disabled,
                  priority: component.sidebar?.priority,
                  routePath: component.route.slice(1),
                  title: component.title,
                  pageCategoryId: component.sidebar?.categoryId,
                  activeSidebarPageId:
                    component.sidebar?.whenActive?.selectedPageId,
                  requiredPermission: component.requiredPermission,
                  previousRoutePaths: component.additionalRoutes,
                  ...(componentSentryDSN
                    ? { errorReporting: { url: componentSentryDSN } }
                    : undefined),
                  ...(await componentSourceBuilder(component)),
                  metaData: component.metaData,
                  iconKey: component.iconKey,
                  pageAnchors: component.pageAnchors,
                  slotIds: component.slotIds,
                  exposureAndMaturity: component.exposureAndMaturity,
                  exportedRouteName: component.exportedRouteName,
                  layoutMode: component.layoutMode,
                },
              },
              compName: component.title,
              experiment: getExperiment(component, appId),
            };
          },
          DashboardPlugin: async (
            component
          ): Promise<BackofficeExtensionWidget> => ({
            compId: component.id,
            compType: ComponentType.BACK_OFFICE_EXTENSION_WIDGET as const,
            compData: {
              backOfficeExtensionWidget: {
                hostingPlatform: getHostingPlatform(component.hostingPlatform),
                extends: component.extends,
                title: component.title,
                requiredPermission: component.requiredPermission,
                width: component.width,
                height: component.height,
                ...(await componentSourceBuilder(component)),
                ...(component.sentryDSN
                  ? { errorReporting: { url: component.sentryDSN } }
                  : undefined),
              },
            },
            compName: component.title,
          }),
          DashboardMenuPlugin: async (
            component
          ): Promise<BackofficeExtensionMenuItem> => ({
            compId: component.id,
            compType: ComponentType.BACK_OFFICE_EXTENSION_MENU_ITEM as const,
            compData: {
              backOfficeExtensionMenuItem: {
                hostingPlatform: getHostingPlatform(component.hostingPlatform),
                id: component.id,
                title: component.title,
                subtitle: component.subtitle,
                iconKey: component.iconKey,
                extends: component.extends,
                action: component.action,
                requiredPermission: component.requiredPermission,
                ...(component.sentryDSN
                  ? { errorReporting: { url: component.sentryDSN } }
                  : undefined),
              },
            },
            compName: component.title,
          }),
          DashboardModal: async (component): Promise<BackofficeModal> => ({
            compId: component.id,
            compType: ComponentType.BACK_OFFICE_MODAL as const,
            compData: {
              backOfficeModal: {
                hostingPlatform: getHostingPlatform(component.hostingPlatform),
                title: component.title,
                requiredPermission: component.requiredPermission,
                width: component.width,
                height: component.height,
                ...(await componentSourceBuilder(component)),
                ...(component.sentryDSN
                  ? { errorReporting: { url: component.sentryDSN } }
                  : undefined),
                metaData: component.metaData,
                iconKey: component.iconKey,
                slotIds: component.slotIds,
                exposureAndMaturity: component.exposureAndMaturity,
                modalParams: component.modalParams,
              },
            },
            compName: component.title,
          }),
          DashboardSidebarCategory: async (
            component
          ): Promise<BackofficeSideCategory> => ({
            compId: component.id,
            compType: ComponentType.BACK_OFFICE_SIDEBAR_CATEGORY as const,
            compData: {
              backOfficeSidebarCategory: {
                hostingPlatform: getHostingPlatform(component.hostingPlatform),
                title: component.label,
                priority: component.priority,
              },
            },
            compName: component.label,
          }),
          EmbeddedScript: async (component): Promise<EmbeddedScript> => ({
            compId: component.id,
            compType: ComponentType.EMBEDDED_SCRIPT as const,
            compName: component.name,
            compData: {
              embeddedScriptComponentData: {
                name: component.name,
                pages: EmbeddedScriptPages.NONE_PAGES,
                placement: component.placement,
                connectArticleUrl: '',
                embedCategory: component.scriptType,
                dependencies: component.dependencies,
                loadOnce: true,
                allowedForFreeSite: true,
                ...(await componentSourceBuilder(component)),
              },
            },
          }),
          CustomElement: async (component): Promise<CustomElement> => {
            const { scriptUrl, settingsUrl } =
              await componentSourceBuilder(component);

            return createCustomElementConfig(component, scriptUrl, settingsUrl);
          },
          BlocksWidget: async (component): Promise<BlocksWidget> => {
            const { componentUrl } = await componentSourceBuilder(component);

            return {
              compId: component.id,
              compType: ComponentType.STUDIO_WIDGET,
              compName: '',
              compData: {
                studioWidgetComponentData: {
                  widgetBundleUrl: componentUrl,
                },
              },
            };
          },
          BlocksMainComponent: async (
            component
          ): Promise<BlocksMainComponent> => {
            return {
              compId: component.id,
              compType: ComponentType.STUDIO,
              compName: 'Blocks Main Component',
              compData: {},
            };
          },
          BlocksWidgetPanel: async (component): Promise<PanelComponent> => {
            const { componentUrl } = await componentSourceBuilder(component);
            const url = new URL('https://BLOCKS_WIDGET_URL.mock');
            url.searchParams.set('codeBundleUrl', componentUrl);

            return {
              compId: component.id,
              compType: ComponentType.PANEL,
              compName: '',
              compData: {
                panel: {
                  url: url.toString(),
                  contentType: PanelContentType.url,
                },
              },
            };
          },
          Topology: async (component): Promise<Topology> => {
            return {
              compId: component.id,
              compType: ComponentType.TOPOLOGY as const,
              compName: component.name,
              compData: {
                topology: {
                  topology: {
                    backendApiUrl: component.backendApiUrl,
                  },
                },
              },
            };
          },
          Api: () => null,
          WebMethod: () => null,
          Event: async (component): Promise<Webhook[]> => {
            const webhookSlugs = await componentSourceBuilder(component);

            return webhookSlugs.map(({ webhookSlug }) => ({
              compName: webhookSlug,
              compId: randomUUID(),
              compType: ComponentType.WEBHOOK,
              compData: {
                webhook: {
                  callbackUrl: '__WEBHOOKS_URL_PLACEHOLDER__',
                  webhookSlug,
                },
              },
            }));
          },
          SitePlugin: async (
            component
          ): Promise<Array<SitePlugin | CustomElement>> => {
            const { scriptUrl, settingsUrl } =
              await componentSourceBuilder(component);

            const customElementConfig = createCustomElementConfig(
              component,
              scriptUrl,
              settingsUrl
            );

            return [
              customElementConfig,
              {
                compId: component.id,
                compType: ComponentType.WIDGET_PLUGIN as const,
                compName: component.marketData.name,
                compData: {
                  widgetPlugin: {
                    referenceComponentId: component.referenceComponentId,
                    placements: component.placements,
                    marketData: {
                      ...component.marketData,
                      logoUrl:
                        component.marketData.logoUrl &&
                        parseImageField(
                          component.marketData.logoUrl,
                          component.relativePath
                        ),
                    },
                    installation: {
                      base: {
                        autoAdd: component.installation?.autoAddToSite ?? true,
                      },
                    },
                  },
                },
              },
            ];
          },
          ServicePlugin: async (component): Promise<ServicePlugin> => {
            const servicePlugin = match(component.servicePlugin, {
              EcomShippingRates: (component): EcomShippingRates => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_SHIPPING_RATES,
                compData: {
                  ecomShippingRates: {
                    name: component.name,
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    description: component.description,
                    learnMoreUrl: component.learnMoreUrl,
                    dashboardUrl: component.dashboardUrl,
                    fallbackDefinitionMandatory:
                      component.fallbackDefinitionMandatory,
                    thumbnailUrl: component.thumbnailUrl,
                  },
                },
              }),
              StaffSortingProvider: (component): StaffSortingProvider => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.STAFF_SORTING_PROVIDER,
                compData: {
                  staffSortingProvider: {
                    deploymentUri: {
                      baseUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    },
                    methodName: component.methodName,
                    methodDescription: component.methodDescription,
                    dashboardPluginId: component.dashboardPluginId,
                  },
                },
              }),
              BookingsPricingProvider: (
                component
              ): BookingsPricingProvider => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.BOOKINGS_PRICING_PROVIDER,
                compData: {
                  bookingsPricingProvider: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    pricingProviderName: component.pricingProviderName,
                  },
                },
              }),
              EcomAdditionalFees: (component): EcomAdditionalFees => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_ADDITIONAL_FEES,
                compData: {
                  ecomAdditionalFees: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                  },
                },
              }),
              EcomDiscountsTrigger: (component): EcomDiscountsTrigger => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_DISCOUNTS_TRIGGER,
                compData: {
                  ecomDiscountsTrigger: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                  },
                },
              }),
              EcomValidations: (component): EcomValidations => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_VALIDATIONS,
                compData: {
                  ecomValidations: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    validateInCart: component.validateInCart,
                  },
                },
              }),
              EcomPaymentSettings: (component): EcomPaymentSettings => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_PAYMENT_SETTINGS,
                compData: {
                  ecomPaymentSettings: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    fallbackValueForRequires3dSecure:
                      component.fallbackValueForRequires3dSecure,
                  },
                },
              }),
              EcomGiftCardsProvider: (component): EcomGiftCardsProvider => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.GIFT_CARDS_PROVIDER,
                compData: {
                  giftCardsProvider: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                  },
                },
              }),
              EcomRecommendationsProvider: (
                component
              ): EcomRecommendationsProvider => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.ECOM_RECOMMENDATIONS_PROVIDER,
                compData: {
                  ecomRecommendationsProvider: {
                    deploymentUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    catalogAppIds: component.catalogAppIds,
                    supportedAlgorithms: component.supportedAlgorithms,
                  },
                },
              }),
              SeoKeywordsSuggestion: (component): SeoKeywordsSuggestion => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.SEO_KEYWORDS_SUGGESTIONS,
                compData: {
                  seoKeywordsSuggestions: {
                    baseUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    upgradeUrl: component.upgradeUrl,
                    quotaEnabled: component.quotaEnabled,
                    supportedCountryCodes: component.supportedCountryCodes,
                    landingPageUrl: component.landingPageUrl,
                  },
                },
              }),
              ExternalDatabaseProvider: (
                component
              ): ExternalDatabaseProvider => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.EXTERNAL_DATABASE_PROVIDER,
                compData: {
                  externalDatabaseProvider: {
                    namespace: component.namespace,
                    uriConfig: {
                      baseUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__',
                    },
                  },
                },
              }),
              PremiumCustomCharges: (component): PremiumCustomCharges => ({
                compName: component.name,
                compId: component.id,
                compType: ComponentType.PREMIUM_CUSTOM_CHARGES,
                compData: {
                  premiumCustomCharges: {
                    baseUri: { baseUri: '__SERVICE_PLUGIN_URL_PLACEHOLDER__' },
                  },
                },
              }),
            });

            const { servicePluginType } =
              await componentSourceBuilder(component);

            if (servicePlugin.compType !== servicePluginType) {
              throw new CliError({
                code: CliErrorCode.InvalidServicePluginImplementation({
                  configuredImplementation: servicePlugin.compType,
                }),
                cause: null,
              });
            }

            return servicePlugin;
          },
          Monitoring: async (component): Promise<Monitoring> => {
            return {
              compName: 'Monitoring',
              compId: component.id,
              compType: ComponentType.MONITORING as const,
              compData: {
                monitoring: getMonitoringProvider(component),
              },
            };
          },
          SiteComponent: async (component): Promise<SiteComponent> => {
            const siteComponentSource = await componentSourceBuilder(component);

            return {
              compId: component.id,
              compType: ComponentType.EDITOR_REACT_COMPONENT as const,
              compName: component.editorElement.displayName || component.id,
              compData: {
                editorReactComponent: {
                  type: component.elementType,
                  description: component.description,
                  editorElement: {
                    ...component.editorElement,
                    presets: siteComponentSource.presets,
                    elements: {
                      // We use typecasting due to recursive nature of SiteComponentElements types
                      ...((component.editorElement.elements ??
                        {}) as SiteComponentElements),
                      ...siteComponentSource.elements,
                    },
                  },
                  installation: component.installation,
                  resources: {
                    ...siteComponentSource.resources,
                  },
                },
              },
            };
          },
          SiteComponentPanel: async (component): Promise<PanelComponent> => {
            return {
              compId: component.id,
              compType: ComponentType.PANEL,
              compName: component.name,
              compData: {
                panel: {
                  displayName: component.name,
                  code: {
                    ...(await componentSourceBuilder(component)),
                  },
                  size: {
                    height: component.height ?? 150,
                    width: component.width ?? PanelSizeWidth.UNKNOWN_WIDTH,
                  },
                  contentType: PanelContentType.code,
                  panelMode: component.panelMode,
                },
              },
            };
          },
        })
      )
    )
  )
    .filter(isTruthy)
    .flat();

  const uniqueComponents = uniqWith(appManifestComponents, (one, two) => {
    return (
      one.compType === ComponentType.WEBHOOK &&
      two.compType === ComponentType.WEBHOOK &&
      one.compData.webhook.webhookSlug === two.compData.webhook.webhookSlug
    );
  });

  return {
    appId,
    components: uniqueComponents,
  };
}
